import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const SuccessStoriesSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('players');

  const playerStories = [
  {
    name: 'Emmanuel Osei',
    from: 'Ghana Premier League',
    to: 'Borussia Dortmund',
    transferValue: '€6.2M',
    year: 2025,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1eb389471-1765721123868.png",
    imageAlt: 'Emmanuel Osei in yellow Borussia Dortmund jersey celebrating goal with teammates in packed stadium showing joy and athletic achievement',
    quote: 'ProX11Market transformed my career. Their professional approach and European network opened doors I never thought possible.',
    achievements: ['Bundesliga Debut', '8 Goals in First Season', 'Ghana National Team Call-up']
  },
  {
    name: 'Chibueze Nwankwo',
    from: 'Nigerian Professional League',
    to: 'AC Milan',
    transferValue: '€4.8M',
    year: 2025,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_157761bc4-1768200409063.png",
    imageAlt: 'Chibueze Nwankwo in red and black AC Milan striped jersey dribbling ball with focused expression showing technical skill on San Siro pitch',
    quote: 'The guidance and support throughout the transfer process was exceptional. They handled everything professionally.',
    achievements: ['Serie A Top Scorer', 'UEFA Champions League', 'African Player of Month']
  },
  {
    name: 'Amadou Diallo',
    from: 'Senegalese Championship',
    to: 'Olympique Lyon',
    transferValue: '€7.5M',
    year: 2026,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19d46039f-1768671662646.png",
    imageAlt: 'Amadou Diallo in white Olympique Lyon jersey making defensive tackle with strong athletic form and determined expression on French stadium field',
    quote: 'From Dakar to Lyon, ProX11Market made my European dream a reality with their expertise and dedication.',
    achievements: ['Ligue 1 Defender of Year', 'Senegal Captain', 'Clean Sheet Record']
  }];


  const clubStories = [
  {
    club: 'Borussia Dortmund',
    country: 'Germany',
    players: 3,
    totalValue: '€18.5M',
    image: "https://images.unsplash.com/photo-1618515150063-1ec2483caf72",
    imageAlt: 'Borussia Dortmund Signal Iduna Park stadium exterior with iconic yellow wall and modern architecture on match day with fans gathering',
    testimonial: 'ProX11Market consistently delivers high-quality African talent with verified credentials and professional documentation.',
    contact: 'Klaus Schmidt',
    role: 'Technical Director'
  },
  {
    club: 'AC Milan',
    country: 'Italy',
    players: 2,
    totalValue: '€12.3M',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14f718ce0-1766881111580.png",
    imageAlt: 'AC Milan San Siro stadium iconic red and black exterior with historic architecture and championship banners displayed prominently',
    testimonial: 'Their market intelligence and player analytics have been invaluable in our African scouting strategy.',
    contact: 'Marco Rossi',
    role: 'Sporting Director'
  }];


  return (
    <section className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(0,0,0,0.15) 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-100 to-yellow-100 rounded-full mb-6 shadow-sm">
            <Icon name="Trophy" size={18} className="text-amber-600" />
            <span className="text-sm font-bold text-slate-800">{t('homepage.successStoriesSection.successStoriesBadge')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-black text-slate-900 mb-6 leading-tight">
            <span className="bg-gradient-to-r from-amber-600 via-yellow-600 to-amber-600 bg-clip-text text-transparent">
              {t('homepage.successStoriesSection.transformingCareers')}
            </span>
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            {t('homepage.successStoriesSection.realResults')}
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center justify-center mb-12">
          <div className="inline-flex bg-white border-2 border-slate-200 rounded-2xl p-1.5 shadow-lg">
            <button
              onClick={() => setActiveTab('players')}
              className={`px-8 py-3 rounded-xl text-base font-bold transition-all duration-300 ${
              activeTab === 'players' ? 'bg-gradient-to-r from-blue-600 to-emerald-600 text-white shadow-lg' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`
              }>
              {t('homepage.successStoriesSection.playerStories')}
            </button>
            <button
              onClick={() => setActiveTab('clubs')}
              className={`px-8 py-3 rounded-xl text-base font-bold transition-all duration-300 ${
              activeTab === 'clubs' ? 'bg-gradient-to-r from-blue-600 to-emerald-600 text-white shadow-lg' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`
              }>
              {t('homepage.successStoriesSection.clubTestimonials')}
            </button>
          </div>
        </div>

        {/* Player Stories */}
        {activeTab === 'players' &&
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12">
            {playerStories?.map((story, index) =>
          <div
            key={index}
            className="group bg-white border-2 border-slate-200 rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">

                {/* Player Image */}
                <div className="relative h-64 overflow-hidden">
                  <Image
                src={story?.image}
                alt={story?.imageAlt}
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
                  
                  {/* Transfer Info Badges */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-2">
                    <div className="bg-white/95 backdrop-blur-xl rounded-xl px-3 py-2 shadow-lg">
                      <div className="text-xs text-slate-600 font-semibold">{t('homepage.successStoriesSection.transferValue')}</div>
                      <div className="text-base font-black bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent">{story?.transferValue}</div>
                    </div>
                    <div className="bg-white/95 backdrop-blur-xl rounded-xl px-3 py-2 shadow-lg">
                      <div className="text-xs text-slate-600 font-semibold">{t('homepage.successStoriesSection.year')}</div>
                      <div className="text-base font-black text-slate-900">{story?.year}</div>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <h3 className="text-2xl font-black text-slate-900 mb-2">{story?.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-slate-600 font-medium mb-4">
                    <span>{story?.from}</span>
                    <Icon name="ArrowRight" size={16} className="text-blue-600" />
                    <span className="font-bold text-blue-600">{story?.to}</span>
                  </div>

                  {/* Quote */}
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 mb-4">
                    <Icon name="Quote" size={16} className="text-slate-400 mb-2" />
                    <p className="text-sm text-slate-700 italic leading-relaxed">"{story?.quote}"</p>
                  </div>

                  {/* Achievements */}
                  <div className="space-y-2">
                    <div className="text-xs font-black text-slate-900 mb-3 uppercase tracking-wide">{t('homepage.successStoriesSection.achievements')}</div>
                    {story?.achievements?.map((achievement, idx) =>
                <div key={idx} className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mt-0.5">
                          <Icon name="Award" size={12} color="white" />
                        </div>
                        <span className="text-sm text-slate-700 font-medium">{achievement}</span>
                      </div>
                )}
                  </div>
                </div>
              </div>
          )}
          </div>
        }

        {/* Club Stories */}
        {activeTab === 'clubs' &&
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12">
            {clubStories?.map((story, index) =>
          <div
            key={index}
            className="group bg-white border-2 border-slate-200 rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-500">

                {/* Club Image */}
                <div className="relative h-72 overflow-hidden">
                  <Image
                src={story?.image}
                alt={story?.imageAlt}
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
                  
                  {/* Club Info */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="text-3xl font-black text-white mb-2">{story?.club}</h3>
                    <p className="text-base text-white/90 font-semibold">{story?.country}</p>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-8">
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-5 text-center border-2 border-blue-200">
                      <div className="text-4xl font-black bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent mb-1">{story?.players}</div>
                      <div className="text-sm text-blue-700 font-bold">{t('homepage.successStoriesSection.players')}</div>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-5 text-center border-2 border-emerald-200">
                      <div className="text-4xl font-black bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent mb-1">{story?.totalValue}</div>
                      <div className="text-sm text-emerald-700 font-bold">{t('homepage.successStoriesSection.totalValue')}</div>
                    </div>
                  </div>

                  {/* Testimonial */}
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 mb-6">
                    <Icon name="Quote" size={20} className="text-slate-400 mb-3" />
                    <p className="text-base text-slate-700 italic leading-relaxed mb-4">"{story?.testimonial}"</p>
                  </div>

                  {/* Contact Info */}
                  <div className="flex items-center gap-4 pt-6 border-t-2 border-slate-200">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-600 to-emerald-600 flex items-center justify-center shadow-lg">
                      <Icon name="User" size={24} color="white" />
                    </div>
                    <div>
                      <div className="text-base font-black text-slate-900">{story?.contact}</div>
                      <div className="text-sm text-slate-600 font-semibold">{story?.role}</div>
                    </div>
                  </div>
                </div>
              </div>
          )}
          </div>
        }

        {/* View All Button */}
        <div className="flex justify-center">
          <Button
            onClick={() => navigate('/success-stories')}
            variant="default"
            size="lg"
            iconName="ArrowRight"
            iconPosition="right"
            className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white shadow-xl border-0 text-base font-bold px-10 py-6">
            {t('homepage.successStoriesSection.viewAllStories')}
          </Button>
        </div>
      </div>
    </section>);

};

export default SuccessStoriesSection;